import { useQuery } from "@tanstack/react-query";
import type React from "react";

import type { PollDto, UserDto } from "@/api/types";
import { Globe02Icon } from "@/components/Icons/Globe02Icon";
import { Star01Icon } from "@/components/Icons/Star01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { AutoTranslatedLabel } from "@/components/Label/AutoTranslatedLabel";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { User } from "@/components/User/User";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePollQueries } from "@/queries/polls/queryOptions";
import { useTranslation } from "@/translations";

type CommunityPollResultModalProps = ModalBaseProps & {
  poll: PollDto;
  isTranslated: boolean;
  onToggleTranslation: () => void;
};

export function CommunityPollResultModal({
  poll,
  isOpened,
  onOpenChange,
  isTranslated,
  onToggleTranslation,
}: CommunityPollResultModalProps): React.ReactNode {
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const pollQueries = usePollQueries();

  const hasAutoTranslation = !!poll.autoTranslation;

  const { data: answersData, isLoading } = useQuery({
    ...pollQueries.answers(poll.id),
    enabled: isOpened,
  });

  const { data: fetchedTranslation, isLoading: isLoadingTranslation } = useQuery({
    ...pollQueries.translation({
      pollId: poll.id,
      languageId: sessionUser.autoTranslationLanguage?.id || sessionUser.language.id,
    }),
    enabled: isOpened && isTranslated && !hasAutoTranslation,
  });

  const activeTranslation = isTranslated ? (poll.autoTranslation ?? fetchedTranslation) : undefined;
  const question = activeTranslation?.question ?? poll.question;

  const isTranslatable = sessionUser.autoTranslationLanguage
    ? poll.languageIsoCode !== sessionUser.autoTranslationLanguage.id
    : poll.languageIsoCode !== sessionUser.language.id;
  const canBeTranslated = !hasAutoTranslation && poll.createdBy.id !== sessionUser.id && isTranslatable;
  const showTranslateToggle = (hasAutoTranslation && !poll.deletedAt) || canBeTranslated;

  const displayedResults = activeTranslation?.results ?? answersData?.pollResults ?? [];
  const allVoters = displayedResults.reduce<UserDto[]>((acc, result) => acc.concat(result.voters), []);
  const amountUniqueVoters = new Set(allVoters.map((voter) => voter.id)).size;

  return (
    <Modal.Root
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={t("component.community-poll.result-modal.title")}
      isPending={isLoading}
    >
      <Modal.Body>
        <Stack gap="md">
          <h4 className="text-heading-4">{question}</h4>

          {isTranslated && poll.autoTranslation?.isAutoTranslated && <AutoTranslatedLabel />}

          {showTranslateToggle && (
            <Button
              variant="ghostPrimary"
              data-testid="translate-btn"
              isLoading={isLoadingTranslation}
              onClick={onToggleTranslation}
              icon={<Globe02Icon />}
            >
              {isTranslated
                ? t("component.community-post.content.translate.original")
                : t("component.community-post.content.translate")}
            </Button>
          )}

          <Inline gap="xs" align="center">
            <Users01Icon size={24} />
            <span className="text-caption text-text-secondary">
              {t("component.community-poll.result-modal.label.amount-people-voted", {
                count: amountUniqueVoters,
              })}
            </span>
          </Inline>

          <Stack gap="md" as="ul">
            {displayedResults.map((result) => (
              <Card as="li" key={result.answer.id} borderTone="secondary" density="flush" clip>
                <header className="flex items-center justify-between p-space-md">
                  <span className="text-body">{result.answer.text}</span>
                  <Inline gap="sm" align="center">
                    {result.hasWon && <Star01Icon appearance="filled" size={20} tone="rating" />}
                    <span className="text-body-emphasis">{result.count}</span>
                  </Inline>
                </header>

                {result.voters.length > 0 && (
                  // Limit height of container to reveal 3 and half voters at a time
                  <ul className="flex max-h-card-media flex-col gap-space-md overflow-y-auto border-t border-border-default p-space-md">
                    {result.voters.map((voter) => (
                      <li key={voter.id}>
                        <User user={voter} />
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            ))}
          </Stack>
        </Stack>
      </Modal.Body>

      <Modal.Actions>
        <Modal.Close>
          <Button>{t("common.action.close")}</Button>
        </Modal.Close>
      </Modal.Actions>
    </Modal.Root>
  );
}
